import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Page,
  Card,
  PageActions,
  Text,
  Loading,
  FormLayout,
  TextField,
  Toast,
  Modal,
  Select,
  InlineError,
  InlineStack,
  Button,
  Thumbnail,
  DropZone,
} from "@shopify/polaris";
import axios from "../../Assets/Lib/axios";
import { useParams, useNavigate } from "react-router-dom";
import LoadingScreen from "../../Components/LoadingScreen";
import { EditIcon } from "@shopify/polaris-icons";
import { MultiSelect } from "react-multi-select-component";

const ProjectAddEdit = ({ mode, url }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isSaving, setIsSaving] = useState(mode === "edit");

  const [item, setItem] = useState({
    name: "",
    recurring: "one time",
    type: "",
    health: "",
    start_date: "",
    deadline: "",
    project_progress: "",
    client_satisfaction: "",
    project_price: "",
    current_cost: "",
    current_profit: "",
    projected_cost: "",
    estimated_time: "",
    time_spent: "",
    time_left: "",
    time_efficiency: "",
    image: "",
    country: "",
    people: [],
  });

  const [errors, setErrors] = useState({});
  const [typeOptions, setTypeOptions] = useState([]);
  const [healthOptions] = useState([
    { label: "Good", value: "1" },
    { label: "Warning", value: "2" },
    { label: "Critical", value: "3" },
  ]);

  const handleChange = useCallback((field, value) => {
    setItem((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  }, []);

  const [active, setActive] = useState(false);
  const toggleActive = useCallback(() => setActive((a) => !a), []);
  const toastMarkup = active ? (
    <Toast content="Project saved successfully" onDismiss={toggleActive} />
  ) : null;

  const [isImageUploading, setIsImageUploading] = useState(false);
  const validImageTypes = [
    "image/gif",
    "image/jpeg",
    "image/png",
    "image/webp",
  ];
  const [files, setFiles] = useState();
  const imageKey = useRef("");
  const [imageError, setImageError] = useState("");

  const handleDropZone = useCallback(
    (_dropFiles, acceptedFiles) => setFiles(acceptedFiles[0]),
    [],
  );

  const sendImage = (files) => {
    setIsImageUploading(true);
    const form_data = new FormData();
    form_data.append("entity_type", "project_logo");
    form_data.append("image", files[files.length - 1]);

    axios
      .post(`/s3/save-file`, form_data)
      .then((result) => {
        imageKey.current = result.data.data.file_key;
        setIsImageUploading(false);
        setItem({
          ...item,
          image:
            validImageTypes.indexOf(files[files.length - 1].type) > -1
              ? window.URL.createObjectURL(files[files.length - 1])
              : "",
        });
        setImageError("");
      })
      .catch(() => setIsImageUploading(false));
  };

  const fileUpload = !files && <DropZone.FileUpload />;
  const uploadedFiles = files && (
    <InlineStack gap="200" align="start" blockAlign="center">
      <div>
        {files.name}{" "}
        <Text variant="bodySm" as="p">
          {files.size} bytes
        </Text>
      </div>
    </InlineStack>
  );

  const validateFields = () => {
    const requiredFields = [
      "name",
      "type",
      "status",
      "start_date",
      "deadline",
      "health",
      // "project_progress",
      // "client_satisfaction",
      // "project_price",
      // "current_cost",
      // "current_profit",
      // "projected_cost",
      // "estimated_time",
      // "time_spent",
      // "time_left",
      // "time_efficiency",
      // "image",
    ];
    let newErrors = {};
    requiredFields.forEach((field) => {
      if (!item[field] || item[field].toString().trim() === "") {
        newErrors[field] = "This field is required";
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const [optionsPeople, setOptionsPeople] = useState([]);

  useEffect(() => {
    axios
      .get("/project-types")
      .then((res) => {
        const types = res?.data?.data?.data || [];
        const options = types.map((t) => ({
          label: t.name,
          value: t.id.toString(),
        }));
        setTypeOptions(options);
      })
      .catch((err) => console.log(err));
    axios
      .get("/people?all")
      .then((res) => {
        const people = res?.data?.data || [];
        console.log(people);
        setOptionsPeople(
          people.map((p) => ({
            label: p.name,
            value: String(p.id),
          })),
        );
      })
      .catch((err) => console.log(err));
    if (mode === "edit") {
      setIsSaving(true);
      axios
        .get(`/projects/${id}`)
        .then((res) => {
          const data = res?.data?.data || {};
          setItem({
            name: data.name || "",
            type: data.project_type_id ? String(data.project_type_id) : "",
            health: String(data.health?.id) || "",
            status: String(data.status?.id) || "",
            start_date: data.start_date || "",
            deadline: data.deadline || "",
            project_progress: data.project_progress || "",
            client_satisfaction: data.client_satisfaction || "",
            project_price: data.project_price || "",
            current_cost: data.current_cost || "",
            current_profit: data.current_profit || "",
            projected_cost: data.projected_cost || "",
            estimated_time: data.estimated_time || "",
            time_spent: data.time_spent || "",
            time_left: data.time_left || "",
            time_efficiency: data.time_efficiency || "",
            image: data.logo || "",
            country: data?.country ?? "",
            recurring: data?.recurring ?? "",
            people: data?.people?.length
              ? data.people.map((item) => ({
                  label: item.name,
                  value: String(item.id),
                }))
              : [],
          });
        })
        .finally(() => setIsSaving(false));
    }
  }, []);
  const [optionsCountries, setOptionsCountries] = useState([]);
  useEffect(() => {
    axios
      .get("/settings/countries")
      .then((res) => {
        const countriesData = res?.data?.data || [];
        const options = [
          { label: "All", value: "All" },
          ...countriesData.map((country) => ({
            label: country.name,
            value: country.name,
          })),
        ];
        setOptionsCountries(options);
      })
      .catch((err) => console.log(err));
  }, []);
  const handleSave = () => {
    if (!validateFields()) return;

    setIsSaving(true);
    const formData = new FormData();
    if (mode === "edit") formData.append("_method", "PATCH");
    formData.append("name", item.name);
    formData.append("project_type_id", item.type);
    formData.append("health", item.health);
    formData.append("status", item.status);
    formData.append("start_date", item.start_date);
    formData.append("deadline", item.deadline);
    formData.append("project_progress", item.project_progress);
    formData.append("client_satisfaction", item.client_satisfaction);
    formData.append("project_price", item.project_price);
    formData.append("current_cost", item.current_cost);
    formData.append("current_profit", item.current_profit);
    formData.append("projected_cost", item.projected_cost);
    formData.append("estimated_time", item.estimated_time);
    formData.append("time_spent", item.time_spent);
    formData.append("time_left", item.time_left);
    formData.append("time_efficiency", item.time_efficiency);
    formData.append("country", item.country);
    formData.append("recurring", item.recurring);
    item.people?.length &&
      item.people.forEach((selected, index) =>
        formData.append(`people[${index}]`, selected.value),
      );
    // ✅ Only append image key if uploaded
    if (imageKey.current) {
      formData.append("logo", imageKey.current);
    }

    axios
      .post(mode === "new" ? "/projects" : `/projects/${id}`, formData)
      .then(() => {
        mode === "new" ? navigate("/admin/projects") : toggleActive();
      })
      .catch((error) => console.log(error))
      .finally(() => setIsSaving(false));
  };
  const [activeDelete, setActiveDelete] = useState(false);
  const toggleDeleteModal = useCallback(() => {
    setActiveDelete((a) => !a);
    setIsSaving(false);
  }, []);

  const handleYesDelete = () => {
    setIsSaving(true);
    axios
      .delete(`/projects/${id}`)
      .then(() => {
        navigate(`/admin/projects`);
      })
      .catch(console.error);
  };
  return (
    <Page
      title={`${mode === "new" ? "Add" : "Edit"} Project`}
      backAction={{ onAction: () => navigate(`/admin/projects`) }}
    >
      {isSaving && <Loading />}

      <Card sectioned>
        <FormLayout>
          <TextField
            label="Name"
            value={item.name}
            onChange={(value) => handleChange("name", value)}
            error={errors.name}
          />
          <Select
            label="Recurring"
            options={[
              { label: "One Time", value: "one time" },
              { label: "Retainer", value: "retainer" },
            ]}
            onChange={(value) => handleChange("recurring", value)}
            value={item.recurring}
            placeholder="Please select"
            // error={errors?.country}
            styles={{
              // Fixes the overlapping problem of the compont
              menu: (provided) => ({ ...provided, zIndex: 9999 }),
            }}
          />
          <Select
            label="Country"
            options={optionsCountries}
            onChange={(value) => handleChange("country", value)}
            value={item.country}
            placeholder="Please select"
            error={errors?.country}
            styles={{
              // Fixes the overlapping problem of the compont
              menu: (provided) => ({ ...provided, zIndex: 9999 }),
            }}
          />
          <Select
            label="Status"
            options={[
              { label: "Pending", value: "1" },
              { label: "In Progress", value: "2" },
              { label: "QA", value: "3" },
              { label: "Done", value: "4" },
            ]}
            value={item.status}
            onChange={(value) => handleChange("status", value)}
            error={errors.status}
            placeholder="Please select"
          />
          <Select
            label="Project Health"
            options={healthOptions}
            value={item.health}
            onChange={(value) => handleChange("health", value)}
            error={errors.health}
            placeholder="Please select"
          />
          <Select
            label="Type"
            options={typeOptions}
            value={item.type}
            onChange={(value) => handleChange("type", value)}
            error={errors.type}
            placeholder="Please select"
            labelAction={{
              content: "Edit Types", // 👈 The text shown on the right of the label
              onAction: () => navigate("/admin/projects/types"), // 👈 Button action
              icon: EditIcon,
            }}
          />
          <Text>People</Text>
          <div
            style={{
              position: "relative",
              zIndex: 9999,
              // marginBottom: "400px",
            }}
          >
            <MultiSelect
              options={optionsPeople}
              onChange={(value) => handleChange("people", value)}
              value={item.people}
              placeholder="Please select"
              // disableSearch
            />
          </div>

          <FormLayout.Group>
            <TextField
              label="Start Date"
              type="date"
              value={item.start_date}
              onChange={(value) => handleChange("start_date", value)}
              error={errors.start_date}
            />
            <TextField
              label="Deadline"
              type="date"
              value={item.deadline}
              onChange={(value) => handleChange("deadline", value)}
              error={errors.deadline}
            />
          </FormLayout.Group>

          <FormLayout.Group>
            <TextField
              label="Progress"
              type="number"
              value={item.project_progress}
              onChange={(value) => handleChange("project_progress", value)}
              error={errors.project_progress}
              suffix="%"
            />
            <TextField
              label="Client Satisfaction"
              type="number"
              value={item.client_satisfaction}
              onChange={(value) => handleChange("client_satisfaction", value)}
              error={errors.client_satisfaction}
              suffix="%"
            />
          </FormLayout.Group>

          <FormLayout.Group>
            {/* <TextField
              label="Project Price"
              type="number"
              value={item.project_price}
              onChange={(value) => handleChange("project_price", value)}
              error={errors.project_price}
            /> */}
            <TextField
              label="Current Cost"
              type="number"
              value={item.current_cost}
              onChange={(value) => handleChange("current_cost", value)}
              error={errors.current_cost}
            />
          </FormLayout.Group>

          <FormLayout.Group>
            <TextField
              label="Current Profit"
              type="number"
              value={item.current_profit}
              onChange={(value) => handleChange("current_profit", value)}
              error={errors.current_profit}
            />
            <TextField
              label="Projected Cost"
              type="number"
              value={item.projected_cost}
              onChange={(value) => handleChange("projected_cost", value)}
              error={errors.projected_cost}
            />
          </FormLayout.Group>

          <FormLayout.Group>
            <TextField
              label="Estimated Time"
              type="number"
              value={item.estimated_time}
              onChange={(value) => handleChange("estimated_time", value)}
              error={errors.estimated_time}
              suffix="hrs"
            />
            <TextField
              label="Time Spent"
              type="number"
              value={item.time_spent}
              onChange={(value) => handleChange("time_spent", value)}
              error={errors.time_spent}
              suffix="hrs"
            />
          </FormLayout.Group>

          <FormLayout.Group>
            <TextField
              label="Time Left"
              type="number"
              value={item.time_left}
              onChange={(value) => handleChange("time_left", value)}
              error={errors.time_left}
              suffix="hrs"
            />
            <TextField
              label="Time Efficiency"
              type="number"
              value={item.time_efficiency}
              onChange={(value) => handleChange("time_efficiency", value)}
              error={errors.time_efficiency}
              suffix="%"
            />
          </FormLayout.Group>

          <Text as="p" fontWeight="regular">
            Image
          </Text>
          <div style={{ display: "flex", flexDirection: "row", width: "100%" }}>
            <Thumbnail size="large" source={item.image || ""} />
            <div style={{ width: "100%", marginLeft: "10px" }}>
              <DropZone
                onDrop={handleDropZone}
                onDropAccepted={sendImage}
                allowMultiple={false}
              >
                {uploadedFiles}
                {fileUpload}
              </DropZone>
            </div>
          </div>
          <InlineError message={imageError} />
        </FormLayout>
      </Card>

      <PageActions
        primaryAction={{
          content: "Save",
          onAction: handleSave,
          loading: isSaving,
        }}
        secondaryActions={
          mode === "edit" && (
            <Button
              variant="primary"
              loading={isSaving}
              tone="critical"
              onClick={() => setActiveDelete(true)}
            >
              Delete
            </Button>
          )
        }
      />
      <Modal
        open={activeDelete}
        onClose={toggleDeleteModal}
        title="Delete Item"
        primaryAction={{
          content: "Yes",
          onAction: handleYesDelete,
          loading: isSaving,
        }}
        secondaryActions={[
          {
            content: "No",
            onAction: toggleDeleteModal,
            loading: isSaving,
          },
        ]}
      >
        <Modal.Section>
          <Text>Are you sure you want to delete this item?</Text>
        </Modal.Section>
      </Modal>

      {toastMarkup}
      {isImageUploading ? <LoadingScreen /> : null}
    </Page>
  );
};

export default ProjectAddEdit;
